<template>
    <div class="alerts">
        <div class="panel-heading">
            <h3>Current Plan</h3>
            <div v-if="!isLoading" class="add">
                <b-button
                    size="sm"
                    class="btn plus"
                    :disabled="skipFetchData"
                    @click="addCurrentPlan"
                >
                    &#43;
                </b-button>
            </div>
        </div>

        <loader v-if="isLoading" />

        <div v-else>
            <div v-if="items.length > 0">
                <div class="subheader">
                    <span>Item:</span>
                    <span>Refills:</span>
                </div>

                <div
                    v-for="item in items"
                    v-bind:key="item.id"
                    class="item-row"
                >
                    <input type="hidden" name="id" :value="item.id" />
                    <span>{{
                        decodedHe(item.item.display_name || item.item.name)
                    }}</span>
                    <span>{{ getRefillValue(item) }}</span>

                    <spinner v-if="loadingRecordId === item.id" />
                    <b-dropdown
                        right
                        size="sm"
                        no-caret
                        toggle-class="options-toggle"
                        lazy
                        v-else-if="
                            enableRemove(item) ||
                            (_hasAccess('patient.record.create') &&
                                isItemActive(item)) ||
                            (_hasAccess('patient.record.create') &&
                                item.summary &&
                                item.summary.remainingRefill > 0 &&
                                isItemActive(item))
                        "
                    >
                        <template slot="button-content">
                            <span class="more">&#183;&#183;&#183;</span>
                        </template>

                        <b-dropdown-item
                            v-if="
                                _hasAccess('patient.record.create') &&
                                item.summary &&
                                item.summary.remainingRefill > 0 &&
                                isItemActive(item)
                            "
                            @click.prevent="refillRecord(item)"
                        >
                            <i class="cus-add"></i> New Refill
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="
                                _hasAccess('patient.record.create') &&
                                isItemActive(item)
                            "
                            @click.prevent="duplicateRecord(item)"
                        >
                            <i class="cus-page-copy"></i> Duplicate Record
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="enableRemove(item)"
                            @click.prevent="removePlan(item)"
                        >
                            <i class="cus-delete"></i> Remove from list
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
            <div v-else>
                <div class="subheader">
                    <span>Item:</span>
                    <span>Refills:</span>
                </div>
                <p style="margin: 5px 0;">No records available.</p>
            </div>
        </div>

        <modal-add-current-plan
            ref="modalAddCurrentPlan"
            @onsuccess="fetchData"
        />

        <modal-record ref="modalRecord" />

        <modal-delete
            ref="modalDelete"
            title="Remove Item"
            module="currentPlan"
            ok-text="Proceed"
            @onsuccess="fetchData"
        >
            Are you sure you want to remove this record?
        </modal-delete>
    </div>
</template>

<script>
// $ = window.$; // <-- this was introduced for CI
import axios from "axios";
import ModalAddCurrentPlan from "./modal-current-plan";
import ModalDelete from "./modal-delete";
import ModalRecord from "@/module/commons/record/create-update";
import Records from "@/services/records";
import Spinner from "@/components/elements/spinner";
const entities = require("entities");

export default {
    components: {
        ModalAddCurrentPlan,
        ModalRecord,
        ModalDelete,
        Spinner,
        Loader: () => import("@/components/elements/loader-list")
    },
    data: function () {
        return {
            isLoading: true,
            items: [],
            isModalLoading: false,
            medicalRecords: [],
            filteredMedicalRecords: [],
            selectedItemIds: [],
            isSavingModal: false,
            itemToBeDeleted: null,
            isDeleting: false,
            loadingRecordId: null
        };
    },
    props: {
        patientId: {
            type: String,
            required: true
        },
        permissions: {
            type: Object,
            default() {
                return {};
            }
        },
        loadingPermissions: {
            type: Boolean,
            default: true
        },
        isSuperUser: {
            type: Boolean,
            default: false
        },
        currentBusinessId: {
            type: String
        },
        skipFetchData: {
            type: Boolean,
            default: false
        },
        patientDetails: {
            type: Object
        }
    },
    computed: {
        modalItemList: function () {
            return this.medicalRecords;
        }
    },
    mounted: function () {
        if (this.skipFetchData) {
            this.isLoading = false;
        } else {
            this.fetchData();
        }
    },
    methods: {
        decodedHe: function (string) {
            return entities.decodeHTML(string);
        },
        fetchData: async function () {
            this.isLoading = true;

            const res = await axios.get(
                process.env.PATIENT_SERVICE + this.patientId + "/current-plan"
            );

            this.isLoading = false;

            if (res.data.response.message.code === 200) {
                let arr = res.data.response.resources;

                arr = arr.filter(rec => {
                    // remove record if remaining refill is 0 (record added via medical record)
                    if (rec.summary && rec.summary.remainingRefill === 0) {
                        return false;
                    }
                    return true;
                });

                this.items = _.orderBy(
                    arr,
                    [record => record.item.name.toLowerCase()],
                    ["asc"]
                );
            }
        },
        addCurrentPlan() {
            this.$refs.modalAddCurrentPlan.open(this.patientId);
        },
        getRefillValue: function (item) {
            if (item.summary && _.has(item.summary, "remainingRefill")) {
                return (item.summary.remainingRefill || 0) + " left";
            }

            return "-";
        },
        enableRemove(item) {
            let enable = false;

            if (this._hasAccess("patient.record.delete")) {
                enable = true;
            }

            if (item.summary && item.summary.remainingRefill) {
                enable = false;
            }

            return enable;
        },
        removePlan(item) {
            if (item && item.current_plan_item) {
                this.$refs.modalDelete.open(item.current_plan_item.id);
            }
        },
        async duplicateRecord(record) {
            this.loadingRecordId = record.id;

            const response = await Records.duplicateRecordDetails(record.id);

            if (response) {
                if (response.canDuplicate == 1) {
                    this.$refs.modalRecord.open({
                        ...record,
                        clientID: this.getPatientOwnerId(),
                        action: "duplicate",
                        module: this.MODULE.PATIENT,
                        record_resources: response
                    });
                } else {
                    this._message(response.message, "danger");
                }
            }

            this.loadingRecordId = null;
        },
        async refillRecord(record) {
            this.loadingRecordId = record.id;

            const response = await Records.refillRecordDetails(record.id);

            if (response) {
                if (response.canRefill == 1) {
                    this.$refs.modalRecord.open({
                        ...record,
                        clientID: this.getPatientOwnerId(),
                        action: "refill",
                        module: this.MODULE.PATIENT,
                        record_resources: response
                    });
                } else {
                    this._message(response.message, "danger");
                }
            }

            this.loadingRecordId = null;
        },
        getPatientOwnerId() {
            const { activeOwners } = this.patientDetails;
            if (activeOwners) {
                return activeOwners[0]?.id;
            } else {
                return null;
            }
        },
        isItemActive(record) {
            return record?.item?.isactive && record?.item?.status;
        }
    }
};
</script>

<style scoped>
.alerts {
    margin-bottom: 50px;
}
.panel-heading {
    display: grid;
    grid-template-columns: 1fr 28px;
    background-color: transparent;
    align-items: center;
}
.panel-heading h3 {
    margin: 0;
    margin-bottom: 10px;
    font-size: 1.3em;
}
.subheader {
    display: grid;
    grid-template-columns: 1fr 100px 28px;
}
.subheader span {
    font-weight: bold;
}
.item-row {
    display: grid;
    grid-template-columns: 1fr 100px 28px;
    border-bottom: 1px solid #ececec;
    padding: 10px 0;
    align-items: flex-start;
}
.item-row .btn.more {
    color: #333;
    padding: 2px 0;
    font-weight: bold;
}
.item-row ul.nav-pills li a {
    padding: 5px 0;
}
/* override semula style yang diapply oleh tab-content a */
a.btn {
    color: #333;
    font-weight: normal;
    text-decoration: none;
}
a.btn.btn-primary {
    color: #fff;
}
.alert {
    white-space: nowrap;
}
.alert a {
    text-decoration: underline;
    color: #222;
    font-weight: normal;
    cursor: pointer;
}
a.btn.plus,
a.btn.more {
    border-radius: 0;
}
a.btn.plus {
    padding: 1px 0;
}
.modal-footer-vue {
    padding: 14px 15px 15px;
    margin-bottom: 0;
    text-align: right;
    background-color: #f5f5f5;
    border-top: 1px solid #ddd;
    border-radius: 0 0 6px 6px;
    box-shadow: inset 0 1px 0 #ffffff;
}
::v-deep .options-toggle {
    padding: 0;
    border-radius: 0 !important;
}
.more {
    font-weight: bold;
    font-size: 1.1em;
}
.btn.plus {
    border-radius: 0;
    font-weight: bold;
    font-size: 1.1em;
}
</style>
